/** Number, date and delta formatting shared by every Insights surface. */

const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat("en");

export function fmtNum(n: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  if (opts.compact !== false && Math.abs(n) >= 10_000) return compact.format(n);
  return whole.format(Math.round(n * 10) / 10);
}

export function fmtPct(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

export function fmtMoney(n: number | null | undefined, digits = 2): string {
  if (n === null || n === undefined) return "—";
  return `$${n.toFixed(digits)}`;
}

export function fmtMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export function fmtDuration(secs: number | null | undefined): string {
  if (!secs || secs <= 0) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m ${Math.round(secs % 60)}s`.replace(/ 0s$/, "");
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** "just now", "5m ago", "3d ago", then a date. */
export function relTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  const s = (now - t) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return fmtDate(iso);
}

export function fmtDate(iso: string | null | undefined, withYear?: boolean): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-GB", {
    day: "numeric", month: "short", ...(withYear ?? !sameYear ? { year: "numeric" } : {}),
  });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/** Bucket label for a series key ("2026-09-14") at a day / week / month grain. */
export function fmtBucket(day: string, bucket: string): string {
  const [y, m, d] = day.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (bucket === "month") return date.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export interface Delta {
  pct: number | null;   // null when there is nothing to compare against
  diff: number;
  direction: "up" | "down" | "flat";
}

export function delta(current: number | null | undefined, previous: number | null | undefined): Delta | null {
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  const diff = current - previous;
  const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  return { pct: previous === 0 ? null : (diff / previous) * 100, diff, direction };
}

export function initials(name: string | null | undefined): string {
  const parts = (name || "?").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export function plural(n: number, word: string, pluralWord?: string): string {
  return `${fmtNum(n)} ${n === 1 ? word : pluralWord ?? `${word}s`}`;
}

/** One entry per group, roles joined — someone can be Chair *and* Staff Liaison of the same committee. */
export function mergeGroupRoles<G extends { type: string; id: number; name: string; role: string; status?: string }>(groups: G[]): G[] {
  const out = new Map<string, G>();
  for (const g of groups) {
    const key = `${g.type}:${g.id}:${g.status ?? ""}`;
    const cur = out.get(key);
    if (!cur) out.set(key, { ...g });
    else if (!cur.role.split(" · ").includes(g.role)) cur.role = `${cur.role} · ${g.role}`;
  }
  return [...out.values()];
}
